import { useState, useMemo } from 'react';
import type { SortingState, PaginationState } from '@hs/ui';
import { Button } from '@heroui/react';
import { IconRefresh as RefreshCw } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { PageDataGrid, PageDataGridActionToolbar } from '@/components/layouts/page/PageDataGrid';

type RelatedResource = {
  id?: string;
  name?: string | null;
  code?: string | null;
};

type EntitlementBalanceUsage = {
  id: string;
  resourceDefinitionId?: string;
  resourceDefinition?: RelatedResource | null;
  bucketId?: number;
  usedQuantity?: number;
  updatedAt?: string;
};

const BALANCE_SORT_FIELDS = new Set(['bucketId', 'updatedAt']);

function resourceDefinitionName(row: { resourceDefinition?: RelatedResource | null }) {
  const name = row.resourceDefinition?.name?.trim();
  return name || '—';
}

export function EntitlementBalanceUsageListPanel() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const sortItem = sorting[0];
  const sortBy =
    sortItem && BALANCE_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as 'bucketId' | 'updatedAt')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizEntitlement.balanceUsage.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? { sortBy, sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const rows = (data?.items ?? []) as EntitlementBalanceUsage[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const columnOverrides = useMemo(
    () => ({
      resourceDefinitionId: {
        header: '资源定义',
        allowsSorting: false,
        cell: (row: EntitlementBalanceUsage) => resourceDefinitionName(row),
      },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <PageDataGrid<EntitlementBalanceUsage>
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        source="entitlement_balance_usage"
        data={rows}
        recordCount={total}
        pageCount={pageCount}
        isLoading={isLoading}
        emptyMessage="暂无数据"
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnOverrides={columnOverrides}
        metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
        paginationBar={{}}
      >
        <PageDataGridActionToolbar>
          <Button variant="secondary" onPress={() => void refetch()}>
            <RefreshCw className="size-4" stroke={1.5} />
            刷新
          </Button>
        </PageDataGridActionToolbar>
      </PageDataGrid>
    </div>
  );
}
